// Path: src/components/admin/AdminFaceEnrollModal.tsx
// Description: 管理员人脸管理组件（含活体检测 + 亮度增强 + 真实录制格式）

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Camera, RefreshCw, Check, AlertCircle, Trash2, Video, Sun, SunDim, AlertTriangle } from 'lucide-react';
import { 
  adminEnrollFaceWithMetadata, 
  adminUpdateFaceWithMetadata, 
  adminDeleteFace, 
  getFaceStatus, 
  parseFaceError 
} from '../../lib/api/faceV2';
import type { FaceStatus } from '../../lib/api/faceV2';
import type { User } from '../../lib/api/core';
import {
  getBestRecordingMimeType,
  getRecordingDebugMeta,
  buildFaceRecordUploadPayload,
  validateRecordingDuration,
  logRecordingDebugInfo,
  parseRecordingError,
  RECORDING_ERRORS,
} from '../../lib/utils/videoRecording';
import type { RecordingMetadata } from '../../lib/utils/videoRecording';

type OperationMode = 'enroll' | 'update' | 'delete';

interface AdminFaceEnrollModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: User;
  mode: OperationMode;
  onSuccess?: () => void;
}

const MIN_RECORDING_DURATION_MS = 3000;
const DEFAULT_RECORDING_DURATION = 5;

const AdminFaceEnrollModal: React.FC<AdminFaceEnrollModalProps> = ({
  isOpen,
  onClose,
  user,
  mode,
  onSuccess
}) => {
  const [cameraActive, setCameraActive] = useState(false);
  const [capturedImage, setCapturedImage] = useState<Blob | null>(null);
  const [recordedVideo, setRecordedVideo] = useState<Blob | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [faceStatus, setFaceStatus] = useState<FaceStatus | null>(null);
  
  // 亮度增强相关状态
  const [brightness, setBrightness] = useState(120);
  const [showFlash, setShowFlash] = useState(false);
  const [isEnhancing, setIsEnhancing] = useState(false);
  
  // 录制格式相关状态
  const [requestedMimeType, setRequestedMimeType] = useState('');
  const [actualMimeType, setActualMimeType] = useState('');
  const [recordingStartTime, setRecordingStartTime] = useState<number>(0);
  const [videoWidth, setVideoWidth] = useState(640);
  const [videoHeight, setVideoHeight] = useState(480);
  
  const cameraStreamRef = useRef<MediaStream | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const flashTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const fetchFaceStatus = useCallback(async () => {
    try {
      const response = await getFaceStatus();
      setFaceStatus(response.data);
    } catch (err) {
      console.error('Failed to fetch face status:', err);
    }
  }, []);

  const startCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } }
      });
      cameraStreamRef.current = stream;
      
      const videoTrack = stream.getVideoTracks()[0];
      const settings = videoTrack.getSettings();
      setVideoWidth(settings.width || 640);
      setVideoHeight(settings.height || 480);
      
      setCameraActive(true);
      
      setTimeout(() => {
        if (videoRef.current && cameraStreamRef.current) {
          videoRef.current.srcObject = cameraStreamRef.current;
          videoRef.current.play().catch(e => {
            console.error('Video play error:', e);
          });
        }
      }, 100);
    } catch (err: any) {
      const errorMsg = parseRecordingError(err);
      setError(errorMsg);
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (cameraStreamRef.current) {
      cameraStreamRef.current.getTracks().forEach(track => track.stop());
      cameraStreamRef.current = null;
    }
    if (recordingIntervalRef.current) {
      clearInterval(recordingIntervalRef.current);
      recordingIntervalRef.current = null;
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    if (flashTimeoutRef.current) {
      clearTimeout(flashTimeoutRef.current);
      flashTimeoutRef.current = null;
    }
    setCameraActive(false);
    setCapturedImage(null);
    setRecordedVideo(null);
    setIsRecording(false);
    setRecordingTime(0);
    setShowFlash(false);
  }, []);

  const startRecording = useCallback(() => {
    if (!cameraStreamRef.current) return;
    
    const capability = getBestRecordingMimeType();
    const mimeType = capability.requestedMimeType;
    
    setRequestedMimeType(mimeType);
    
    console.log('[管理员录制能力探测]', {
      支持的格式: capability.supportedMimeTypes,
      选择的格式: mimeType
    });
    
    let mediaRecorder: MediaRecorder;
    
    try {
      if (mimeType && MediaRecorder.isTypeSupported(mimeType)) {
        mediaRecorder = new MediaRecorder(cameraStreamRef.current, { mimeType });
      } else {
        mediaRecorder = new MediaRecorder(cameraStreamRef.current);
      }
    } catch (err) {
      console.error('MediaRecorder creation failed:', err);
      setError(RECORDING_ERRORS.RECORDING_START_FAILED);
      return;
    }
    
    const actualMime = mediaRecorder.mimeType;
    setActualMimeType(actualMime);
    
    const chunks: Blob[] = [];
    
    mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) {
        chunks.push(e.data);
      }
    };
    
    mediaRecorder.onstop = () => {
      const blob = new Blob(chunks, { type: actualMime });
      setRecordedVideo(blob);
      setIsRecording(false);
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
        recordingIntervalRef.current = null;
      }
      
      logRecordingDebugInfo({
        requestedMimeType: mimeType,
        actualMimeType: actualMime,
        blobType: blob.type,
        fileName: `face_record.${actualMime.includes('mp4') ? 'mp4' : 'webm'}`,
        fileSize: blob.size,
        durationMs: Date.now() - recordingStartTime,
      });
    };
    
    mediaRecorder.start();
    mediaRecorderRef.current = mediaRecorder;
    setIsRecording(true);
    setRecordingTime(0);
    setRecordingStartTime(Date.now());
    
    recordingIntervalRef.current = setInterval(() => {
      setRecordingTime(prev => {
        if (prev >= DEFAULT_RECORDING_DURATION - 1) {
          if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
            mediaRecorderRef.current.stop();
          }
          return DEFAULT_RECORDING_DURATION;
        }
        return prev + 1;
      });
    }, 1000);
  }, [recordingStartTime]);

  const captureImage = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;
    
    setIsEnhancing(true);
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    
    if (!context) return;
    
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    setShowFlash(true);
    flashTimeoutRef.current = setTimeout(() => {
      setShowFlash(false);
    }, 150);
    
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    
    const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;
    const factor = brightness / 100;
    
    for (let i = 0; i < data.length; i += 4) {
      data[i] = Math.min(255, data[i] * factor);
      data[i + 1] = Math.min(255, data[i + 1] * factor);
      data[i + 2] = Math.min(255, data[i + 2] * factor);
    }
    
    context.putImageData(imageData, 0, 0);
    
    canvas.toBlob((blob) => {
      if (blob) {
        setCapturedImage(blob);
      }
      setIsEnhancing(false);
    }, 'image/jpeg', 0.9);
  }, [brightness]);

  const handleSubmit = async () => {
    if (mode === 'delete') {
      setIsSubmitting(true);
      setError('');
      
      try {
        const response = await adminDeleteFace(user.id);
        
        if (response.data.success) {
          setSuccess(response.data.message);
          if (onSuccess) {
            setTimeout(() => onSuccess(), 1500);
          }
        } else {
          setError(response.data.message);
        }
      } catch (err: any) {
        const errorMsg = parseFaceError(err);
        setError(errorMsg);
      } finally {
        setIsSubmitting(false);
      }
      return;
    }
    
    if (!recordedVideo) {
      setError('请先录制视频');
      return;
    }
    
    if (!capturedImage) {
      setError('请先拍摄人脸照片');
      return;
    }
    
    const durationMs = Date.now() - recordingStartTime;
    const validation = validateRecordingDuration(durationMs, MIN_RECORDING_DURATION_MS);
    
    if (!validation.valid) {
      setError(validation.message);
      return;
    }
    
    setIsSubmitting(true);
    setError('');
    
    try {
      const metadata: RecordingMetadata = getRecordingDebugMeta({
        requestedMimeType,
        actualMimeType,
        durationMs,
        videoWidth,
        videoHeight,
        facingMode: 'user',
      });
      
      const formData = buildFaceRecordUploadPayload(recordedVideo, capturedImage, metadata);
      
      console.log('[管理员上传元数据]', metadata);
      
      let response;
      if (mode === 'enroll') {
        response = await adminEnrollFaceWithMetadata(user.id, formData);
      } else {
        response = await adminUpdateFaceWithMetadata(user.id, formData);
      }
      
      if (response.data.success) {
        setSuccess(response.data.message);
        if (onSuccess) {
          setTimeout(() => onSuccess(), 1500);
        }
      } else {
        setError(response.data.message);
      }
    } catch (err: any) {
      const errorMsg = parseFaceError(err);
      setError(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchFaceStatus();
      if (mode !== 'delete') {
        startCamera();
      }
    } else {
      stopCamera();
      setCapturedImage(null);
      setRecordedVideo(null);
      setError('');
      setSuccess('');
    }
    return () => stopCamera();
  }, [isOpen, mode, fetchFaceStatus, startCamera, stopCamera]);

  if (!isOpen) return null;

  const getModeTitle = () => {
    switch (mode) {
      case 'enroll': return '录入人脸';
      case 'update': return '修改人脸';
      case 'delete': return '删除人脸';
    }
  };

  const getModeButton = () => {
    switch (mode) {
      case 'enroll': return '确认录入';
      case 'update': return '确认修改';
      case 'delete': return '确认删除';
    }
  };

  const filterStyle = {
    filter: `brightness(${brightness}%) contrast(${Math.min(110, brightness * 0.9)}%)`,
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      {showFlash && (
        <div 
          className="fixed inset-0 bg-white z-[60] pointer-events-none"
          style={{ 
            animation: 'flash 0.15s ease-out forwards',
            opacity: 0.8
          }}
        />
      )}
      
      <div className="relative w-[420px] max-h-[90vh] overflow-y-auto bg-gray-900 rounded-xl p-6 shadow-2xl border border-white/10">
        <button 
          onClick={onClose} 
          className="absolute top-3 right-3 text-gray-400 hover:text-white transition-colors"
        >
          <X size={20} />
        </button>
        
        <h3 className="text-lg font-semibold text-white mb-4">{getModeTitle()} - {user.username}</h3>
        
        <div className="mb-4 p-3 bg-white/5 rounded-lg text-sm border border-white/10">
          <div className="grid grid-cols-2 gap-2 text-white/70">
            <div><span className="text-white/50">用户名:</span> {user.username}</div>
            <div><span className="text-white/50">角色:</span> {user.role}</div>
            <div className="col-span-2"><span className="text-white/50">学校:</span> {user.school?.schoolname || '未绑定'}</div>
          </div>
        </div>
        
        {faceStatus && (
          <div className={`mb-4 p-3 rounded-lg text-sm ${faceStatus.has_face ? 'bg-green-500/10 border border-green-500/20' : 'bg-yellow-500/10 border border-yellow-500/20'}`}>
            <div className="flex items-center gap-2">
              {faceStatus.has_face ? (
                <>
                  <Check size={16} className="text-green-400" />
                  <span className="text-green-400">已录入人脸</span>
                  {faceStatus.quality_score && (
                    <span className="text-green-400/70 ml-2">质量: {(faceStatus.quality_score * 100).toFixed(0)}%</span>
                  )}
                </>
              ) : (
                <>
                  <AlertCircle size={16} className="text-yellow-400" />
                  <span className="text-yellow-400">未录入人脸</span>
                </>
              )}
            </div>
          </div>
        )}
        
        {error && (
          <div className="mb-4 bg-red-500/10 border border-red-500/20 text-red-400 px-3 py-2 rounded text-sm">
            {error}
          </div>
        )}
        
        {success && (
          <div className="mb-4 bg-green-500/10 border border-green-500/20 text-green-400 px-3 py-2 rounded text-sm">
            {success}
          </div>
        )}
        
        {mode === 'delete' ? (
          <div className="mb-4 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
            <div className="flex items-center gap-2 text-red-400 mb-2">
              <AlertCircle size={20} />
              <span className="font-semibold">确认删除人脸数据？</span>
            </div>
            <p className="text-red-400/70 text-sm">
              删除后，该用户将无法使用人脸识别登录。此操作不可撤销。
            </p>
          </div>
        ) : (
          <>
            {cameraActive && (
              <div className="mb-3 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2 text-xs text-amber-400">
                    <Sun size={14} />
                    <span className="font-medium">画面亮度增强</span>
                  </div>
                  <span className="text-xs font-mono text-amber-400/70">{brightness}%</span>
                </div>
                <div className="flex items-center gap-2">
                  <SunDim size={14} className="text-amber-400/50" />
                  <input
                    type="range"
                    min="80"
                    max="180"
                    value={brightness}
                    onChange={(e) => setBrightness(Number(e.target.value))}
                    className="flex-1 h-2 bg-amber-500/20 rounded-lg appearance-none cursor-pointer accent-amber-500"
                  />
                  <Sun size={14} className="text-amber-400" />
                </div>
              </div>
            )}
            
            {actualMimeType && (
              <div className="mb-3 p-2 bg-white/5 border border-white/10 rounded text-xs text-white/60">
                <span className="font-medium">录制格式：</span>
                {actualMimeType.includes('mp4') ? (
                  <span className="text-green-400">MP4 (推荐)</span>
                ) : (
                  <span className="text-amber-400">WebM (系统将自动转换)</span>
                )}
              </div>
            )}
            
            <div className="relative aspect-[4/3] bg-black rounded-lg overflow-hidden mb-4 border border-white/10">
              {cameraActive ? (
                <>
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className="w-full h-full object-cover transform scale-x-[-1]"
                    style={filterStyle}
                  />
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className={`w-40 h-40 border-2 rounded-full ${isRecording ? 'border-red-400 animate-pulse' : 'border-cyan-400/50'}`}></div>
                  </div>
                  {isRecording && (
                    <div className="absolute top-2 left-1/2 -translate-x-1/2 flex items-center gap-2 text-xs text-red-400 bg-black/50 px-2 py-1 rounded">
                      <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                      录制中: {recordingTime}s / {DEFAULT_RECORDING_DURATION}s
                    </div>
                  )}
                  {recordedVideo && !isRecording && (
                    <div className="absolute bottom-2 left-1/2 -translate-x-1/2 text-xs text-green-400 bg-black/50 px-2 py-1 rounded">
                      ✓ 视频已录制 ({recordingTime}s)
                    </div>
                  )}
                  {capturedImage && (
                    <div className="absolute bottom-2 right-2 text-xs text-green-400 bg-black/50 px-2 py-1 rounded">
                      ✓ 照片已拍摄
                    </div>
                  )}
                  {isEnhancing && (
                    <div className="absolute inset-0 bg-white/30 flex items-center justify-center">
                      <RefreshCw size={24} className="animate-spin text-cyan-500" />
                    </div>
                  )}
                </>
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center text-white/50">
                  <Camera size={48} className="mb-2 opacity-50" />
                  <span className="text-sm">正在启动摄像头...</span>
                </div>
              )}
              <canvas ref={canvasRef} className="hidden" />
            </div>
            
            <div className="space-y-2">
              {cameraActive && !recordedVideo && (
                <button
                  onClick={isRecording ? undefined : startRecording}
                  disabled={!cameraActive || isSubmitting || isRecording}
                  className="w-full flex justify-center items-center gap-2 py-2.5 px-4 border border-cyan-400/30 text-xs font-bold uppercase tracking-widest rounded-lg text-cyan-400 bg-cyan-400/10 hover:bg-cyan-400 hover:text-black focus:outline-none focus:ring-2 focus:ring-cyan-400 transition-all duration-300 disabled:opacity-50"
                >
                  {isRecording ? (
                    <>
                      <RefreshCw size={14} className="animate-spin" />
                      录制中... {recordingTime}s
                    </>
                  ) : (
                    <>
                      <Video size={14} />
                      开始录制 ({DEFAULT_RECORDING_DURATION}秒)
                    </>
                  )}
                </button>
              )}
              
              {cameraActive && recordedVideo && !capturedImage && (
                <button
                  onClick={captureImage}
                  disabled={!cameraActive || isSubmitting || isRecording || isEnhancing}
                  className="w-full flex justify-center items-center gap-2 py-2.5 px-4 border border-cyan-400/30 text-xs font-bold uppercase tracking-widest rounded-lg text-cyan-400 bg-cyan-400/10 hover:bg-cyan-400 hover:text-black focus:outline-none focus:ring-2 focus:ring-cyan-400 transition-all duration-300 disabled:opacity-50"
                >
                  {isEnhancing ? (
                    <>
                      <RefreshCw size={14} className="animate-spin" />
                      处理中...
                    </>
                  ) : (
                    <>
                      <Camera size={14} />
                      拍摄照片
                    </>
                  )}
                </button>
              )}
              
              {recordedVideo && capturedImage && (
                <>
                  {recordingTime < 3 && (
                    <div className="p-2 bg-amber-500/10 border border-amber-500/20 rounded-lg text-xs text-amber-400 flex items-center gap-2">
                      <AlertTriangle size={14} />
                      录制时长不足3秒，建议重新录制
                    </div>
                  )}
                  <button
                    onClick={() => { setRecordedVideo(null); setCapturedImage(null); startCamera(); }}
                    disabled={isSubmitting}
                    className="w-full flex justify-center items-center gap-2 py-2 px-4 border border-white/20 text-xs text-white/70 hover:text-white hover:border-white/40 rounded-lg transition-all disabled:opacity-50"
                  >
                    <RefreshCw size={14} />
                    重新录制
                  </button>
                </>
              )}
            </div>
          </>
        )}
        
        <div className="mt-4 space-y-2">
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || (mode !== 'delete' && (!recordedVideo || !capturedImage))}
            className={`w-full flex justify-center items-center gap-2 py-2.5 px-4 border text-xs font-bold uppercase tracking-widest rounded-lg focus:outline-none focus:ring-2 transition-all duration-300 disabled:opacity-50 ${
              mode === 'delete' 
                ? 'border-red-400/30 text-red-400 bg-red-400/10 hover:bg-red-400 hover:text-black focus:ring-red-400'
                : 'border-green-400/30 text-green-400 bg-green-400/10 hover:bg-green-400 hover:text-black focus:ring-green-400'
            }`}
          >
            {isSubmitting ? (
              <>
                <RefreshCw size={14} className="animate-spin" />
                处理中...
              </>
            ) : (
              <>
                {mode === 'delete' ? <Trash2 size={14} /> : <Check size={14} />}
                {getModeButton()}
              </>
            )}
          </button>
          
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="w-full flex justify-center items-center gap-2 py-2 px-4 border border-white/20 text-xs text-white/70 hover:text-white hover:border-white/40 rounded-lg transition-all disabled:opacity-50"
          >
            取消
          </button>
        </div>
      </div>
      
      <style>{`
        @keyframes flash {
          0% { opacity: 0.8; }
          100% { opacity: 0; }
        }
      `}</style>
    </div>,
    document.body
  );
};

export default AdminFaceEnrollModal;
